import { Typography, DenovoIcon } from "@repo/design-system";
import { useGetVisualAnalysis } from "./hooks";
import {
  HistogramChart,
  VisualAnalysisSkeleton,
  AnalysisTable,
} from "./_components";
import { OUTLIER_MIN_COUNT } from "./_components/HistogramChart";

export function VisualAnalysisPage() {
  const { data, isPending, isError } = useGetVisualAnalysis();

  if (isPending) return <VisualAnalysisSkeleton />;

  if (isError) {
    return (
      <div className="flex min-h-[calc(100vh-6rem)] flex-col items-center justify-center gap-4 bg-backgroundPrimary">
        <DenovoIcon
          icon="alert"
          width={48}
          height={48}
          className="text-contentAlert"
        />
        <Typography variant="heading-4" className="text-contentPrimary">
          Failed to load data
        </Typography>
        <Typography
          variant="paragraph-l-regular"
          className="text-contentSecondary">
          Please check your connection and try again.
        </Typography>
      </div>
    );
  }

  const entries = Object.entries(data.histogramValues);

  return (
    <div className="min-h-[calc(100vh-6rem)] bg-backgroundPrimary p-4 md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col items-center gap-1 text-center">
          <Typography variant="heading-3" className="text-contentPrimary">
            Histograms
          </Typography>
          <Typography
            variant="paragraph-l-regular"
            className="text-contentSecondary">
            Distributions of calculated molecular properties. Molecules with
            Molecular Weight {">"} 1000 are excluded as well as outliers with
            count &lt; {OUTLIER_MIN_COUNT} to improve readability.
          </Typography>
        </div>

        {entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-borderPrimary bg-backgroundSecondary py-16">
            <DenovoIcon
              icon="data"
              width={40}
              height={40}
              className="text-contentTertiary"
            />
            <Typography
              variant="paragraph-l-medium"
              className="text-contentSecondary">
              No data available
            </Typography>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            {entries.map(([colName, values]) => (
              <HistogramChart key={colName} colName={colName} values={values} />
            ))}
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          <AnalysisTable
            title="Top Combinations"
            columns={data.columnsTopCombinations}
            rows={data.topCombinations}
          />
          <AnalysisTable
            title="Maximum Virus Count"
            columns={data.columnsVirusMaxCount}
            rows={data.virusMaxCount}
            wrapCols={{ 1: 2, 2: 1 }}
            headerOnlyCols={[3]}
            centerCols={[3]}
            truncateCols={{ 1: { maxChars: 25, truncateTo: 22 } }}
          />
        </div>
      </div>
    </div>
  );
}
